import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Divider,
  Flex,
  Text,
  VStack,
} from '@chakra-ui/react'
import {
  getDateTimeString,
  getEfwPercentile,
  FieldMapValue,
  UltrasoundData,
  UltrasoundDopplerStudy,
  UltrasoundPlacentalLocation,
} from '@hb/shared'
import { SectionLabel } from '../../SectionLabel'

const LabelText = ({ label }: { label: string }) => (
  <Text fontSize="xs" fontWeight={600} color="gray.500" _dark={{ color: 'gray.400' }} mb={0.5}>
    {label}
  </Text>
)

const dopplerFlag = (dop: UltrasoundDopplerStudy) => {
  if (dop.uaEdFlow === 'reversed' || dop.ductusVenosus === 'reversed-a-wave')
    return { status: 'error' as const, text: 'Reversed end-diastolic flow or reversed DV a-wave.' }
  if (dop.uaEdFlow === 'absent' || dop.ductusVenosus === 'absent-a-wave')
    return { status: 'warning' as const, text: 'Absent end-diastolic flow or absent DV a-wave.' }
  if (dop.cpr != null && dop.cpr < 1.0)
    return {
      status: 'warning' as const,
      text: `CPR ${dop.cpr.toFixed(2)} — cerebroplacental redistribution.`,
    }
  return null
}

const placentaFlag = (loc: UltrasoundPlacentalLocation) => {
  if (loc === 'low-lying')
    return {
      status: 'warning' as const,
      text: 'Low-lying placenta — follow-up ultrasound at 32w recommended.',
    }
  if (loc === 'previa')
    return { status: 'error' as const, text: 'Placenta previa — refer to MFM; pelvic rest.' }
  return null
}

const imageQualityLabel: Record<string, string> = {
  optimal: 'Optimal',
  adequate: 'Adequate',
  'limited-habitus': 'Limited — Habitus',
  'limited-position': 'Limited — Fetal Position',
  'limited-other': 'Limited — Other',
}

export const UltrasoundFindingsView = ({ data }: { data: FieldMapValue }) => {
  const d = data as UltrasoundData
  const hasFindings =
    d.fetalCardiacActivity ||
    d.fetalNumber ||
    d.fetalPresentation ||
    d.placentalLocation ||
    d.clinicalImpression

  if (!hasFindings) return null

  const placenta = d.placentalLocation ? placentaFlag(d.placentalLocation) : null
  const dopplerResult = d.dopplerStudy ? dopplerFlag(d.dopplerStudy) : null
  const isScenarioB = d.scenarioType === 'sonographer-study'

  const efwPct =
    d.fetalBiometry?.efw != null && d.gaAtStudyDays != null
      ? getEfwPercentile(d.fetalBiometry.efw, Math.floor(d.gaAtStudyDays / 7))
      : null

  const efwPctColor = (pct: number) => {
    if (pct < 5 || pct > 95) return 'red'
    if (pct < 10 || pct > 90) return 'orange'
    return 'green'
  }

  return (
    <VStack align="stretch" spacing={3} w="100%">
      <SectionLabel>Findings</SectionLabel>

      <Flex gap={4} flexWrap="wrap">
        {d.fetalNumber && (
          <Box>
            <LabelText label="Fetal Number" />
            <Text fontSize="sm" textTransform="capitalize">
              {d.fetalNumber.replace(/-/g, ' ')}
            </Text>
          </Box>
        )}
        {d.fetalCardiacActivity && (
          <Box>
            <LabelText label="Cardiac Activity" />
            <Badge
              colorScheme={
                d.fetalCardiacActivity === 'present'
                  ? 'green'
                  : d.fetalCardiacActivity === 'absent'
                    ? 'red'
                    : 'gray'
              }
              fontSize="xs">
              {d.fetalCardiacActivity === 'not-assessed' ? 'Not Assessed' : d.fetalCardiacActivity}
            </Badge>
          </Box>
        )}
        {d.fetalPresentation && (
          <Box>
            <LabelText label="Presentation" />
            <Text fontSize="sm" textTransform="capitalize">
              {d.fetalPresentation}
            </Text>
          </Box>
        )}
        {d.placentalLocation && (
          <Box>
            <LabelText label="Placental Location" />
            <Text fontSize="sm" textTransform="capitalize">
              {d.placentalLocation.replace(/-/g, ' ')}
            </Text>
          </Box>
        )}
        {d.amnioticFluidType && d.amnioticFluidValue != null && (
          <Box>
            <LabelText label={d.amnioticFluidType.toUpperCase()} />
            <Text fontSize="sm">{d.amnioticFluidValue} cm</Text>
          </Box>
        )}
        {d.cervicalLength != null && (
          <Box>
            <LabelText label="Cervical Length" />
            <Text fontSize="sm">{d.cervicalLength} mm</Text>
          </Box>
        )}
      </Flex>

      {d.fetalBiometry &&
        (d.fetalBiometry.bpd || d.fetalBiometry.hc || d.fetalBiometry.ac || d.fetalBiometry.fl) && (
          <Box>
            <LabelText label="Fetal Biometry" />
            <Flex gap={3} flexWrap="wrap" fontSize="sm">
              {d.fetalBiometry.bpd != null && <Text>BPD {d.fetalBiometry.bpd} mm</Text>}
              {d.fetalBiometry.hc != null && <Text>HC {d.fetalBiometry.hc} mm</Text>}
              {d.fetalBiometry.ac != null && <Text>AC {d.fetalBiometry.ac} mm</Text>}
              {d.fetalBiometry.fl != null && <Text>FL {d.fetalBiometry.fl} mm</Text>}
              {d.fetalBiometry.efw != null && (
                <Flex align="center" gap={1} wrap="wrap">
                  <Text fontWeight={600}>EFW {d.fetalBiometry.efw} g</Text>
                  {efwPct != null && (
                    <Badge colorScheme={efwPctColor(efwPct)} fontSize="xs">
                      {efwPct}th %ile
                    </Badge>
                  )}
                </Flex>
              )}
            </Flex>
          </Box>
        )}

      {d.dopplerStudy &&
        (d.dopplerStudy.uaPi != null ||
          d.dopplerStudy.uaEdFlow ||
          d.dopplerStudy.mcaPi != null ||
          d.dopplerStudy.mcaPsv != null ||
          d.dopplerStudy.ductusVenosus) && (
          <Box>
            <LabelText label="Doppler Studies" />
            <Flex gap={3} flexWrap="wrap" fontSize="sm">
              {d.dopplerStudy.uaPi != null && <Text>UA PI {d.dopplerStudy.uaPi}</Text>}
              {d.dopplerStudy.uaRi != null && <Text>UA RI {d.dopplerStudy.uaRi}</Text>}
              {d.dopplerStudy.uaSdRatio != null && <Text>UA S/D {d.dopplerStudy.uaSdRatio}</Text>}
              {d.dopplerStudy.uaEdFlow && (
                <Badge
                  colorScheme={
                    d.dopplerStudy.uaEdFlow === 'reversed'
                      ? 'red'
                      : d.dopplerStudy.uaEdFlow === 'absent'
                        ? 'orange'
                        : 'green'
                  }
                  fontSize="xs">
                  ED Flow: {d.dopplerStudy.uaEdFlow}
                </Badge>
              )}
              {d.dopplerStudy.mcaPi != null && <Text>MCA PI {d.dopplerStudy.mcaPi}</Text>}
              {d.dopplerStudy.mcaPsv != null && <Text>MCA PSV {d.dopplerStudy.mcaPsv} cm/s</Text>}
              {d.dopplerStudy.cpr != null && (
                <Badge
                  colorScheme={d.dopplerStudy.cpr < 1.0 ? 'orange' : 'green'}
                  fontWeight={600}
                  fontSize="xs">
                  CPR {d.dopplerStudy.cpr.toFixed(2)}
                </Badge>
              )}
              {d.dopplerStudy.ductusVenosus && (
                <Badge
                  colorScheme={
                    d.dopplerStudy.ductusVenosus === 'reversed-a-wave'
                      ? 'red'
                      : d.dopplerStudy.ductusVenosus === 'absent-a-wave' ||
                          d.dopplerStudy.ductusVenosus === 'abnormal'
                        ? 'orange'
                        : 'green'
                  }
                  fontSize="xs">
                  DV: {d.dopplerStudy.ductusVenosus.replace(/-/g, ' ')}
                </Badge>
              )}
            </Flex>
          </Box>
        )}

      {dopplerResult && (
        <Alert status={dopplerResult.status} borderRadius={4}>
          <AlertIcon />
          <AlertDescription fontSize="sm">{dopplerResult.text}</AlertDescription>
        </Alert>
      )}

      {placenta && (
        <Alert status={placenta.status} borderRadius={4}>
          <AlertIcon />
          <AlertDescription fontSize="sm">{placenta.text}</AlertDescription>
        </Alert>
      )}

      {d.clinicalImpression && (
        <Box>
          <LabelText label="Clinical Impression" />
          <Text fontSize="sm" whiteSpace="pre-wrap">
            {d.clinicalImpression}
          </Text>
        </Box>
      )}

      {d.clinicalImpressionSummary && d.clinicalImpressionSummary !== d.clinicalImpression && (
        <Box>
          <LabelText label="Summary" />
          <Text fontSize="sm">{d.clinicalImpressionSummary}</Text>
        </Box>
      )}

      <Flex gap={2} flexWrap="wrap">
        {d.referralIndicated && (
          <Badge colorScheme="orange" fontSize="xs">
            Referral Indicated
          </Badge>
        )}
        {d.followUpStudyIndicated && (
          <Badge colorScheme="purple" fontSize="xs">
            Follow-Up Study Indicated
          </Badge>
        )}
        {d.anatomySurveyComplete && (
          <Badge colorScheme="green" fontSize="xs">
            Anatomy Survey Complete
          </Badge>
        )}
      </Flex>

      {/* §2.14.3 Scenario B fields */}
      {isScenarioB && (d.imageQuality || d.agreementWithFindings || d.sonographerSignOff) && (
        <>
          <Divider />
          <Text
            fontSize="xs"
            fontWeight={700}
            color="blue.600"
            _dark={{ color: 'blue.300' }}
            textTransform="uppercase"
            letterSpacing="wide">
            Sonographer Study
          </Text>
          <Flex gap={4} flexWrap="wrap">
            {d.imageQuality && (
              <Box>
                <LabelText label="Image Quality" />
                <Text fontSize="sm">{imageQualityLabel[d.imageQuality] ?? d.imageQuality}</Text>
              </Box>
            )}
            {d.agreementWithFindings && (
              <Box>
                <LabelText label="Agreement with Findings" />
                <Badge
                  colorScheme={d.agreementWithFindings === 'agree' ? 'green' : 'orange'}
                  fontSize="xs">
                  {d.agreementWithFindings === 'agree' ? 'Agree' : 'Disagree'}
                </Badge>
              </Box>
            )}
          </Flex>
          {d.agreementWithFindings === 'disagree' && d.disagreementDetail && (
            <Box>
              <LabelText label="Disagreement Detail" />
              <Text fontSize="sm" whiteSpace="pre-wrap">
                {d.disagreementDetail}
              </Text>
            </Box>
          )}
          {d.sonographerSignOff && (
            <Box>
              <LabelText label="Sonographer Sign-Off" />
              <Badge colorScheme="green" fontSize="xs">
                Signed{' '}
                {getDateTimeString(new Date(d.sonographerSignOff.signedAt).getTime(), 'short')}
              </Badge>
            </Box>
          )}
        </>
      )}
    </VStack>
  )
}
